// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rpsc/App/Components/Notify";
import { CustomAppState, DeliveryAccount } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import { SelectPresentationProps } from "@rpsc/MobiusOverrides/Select";
import Select from "@rpsc/MobiusOverrides/Select";
import TextField from "@rpsc/MobiusOverrides/TextField";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import { parseBoolean } from "@rpsc/XDL/Utilities";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const {
        accountSettings: { enableWarehousePickup, allowEmptyShipping },
        cartSettings: { canRequestDeliveryDate, enableRequestPickUpDate, maximumDeliveryPeriod },
    } = getSettingsCollection(state);
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
    return {
        cart,
        isCartLoading,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showRequestPickUpDate:
            enableWarehousePickup && enableRequestPickUpDate && fulfillmentMethod === FulfillmentMethod.PickUp,
        showRequestDeliveryDate: canRequestDeliveryDate,
        maximumDeliveryPeriod,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        allowEmptyShipping,
        // XNG-Change
        shipPartial: (state as CustomAppState).xCheckout.shipPartial,
        deliveryAccount: (state as CustomAppState).xCheckout.deliveryAccount,
        isGuestUser: getCurrentUserIsGuest(state),
        collectSettingId: getSettingsCollection(state).xngShippingSettings?.collectCarrierId,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    // XNG-Change
    setShipPartial: (shipPartial: boolean) => ({
        type: "xCheckout/setShipPartial",
        data: { shipPartial },
    }),
    setDeliveryAccount: (deliveryAccount: Partial<DeliveryAccount>) => ({
        type: "xCheckout/setDeliveryAccount",
        data: { deliveryAccount },
    }),
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDeliveryMethodStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    fulfillmentMethodText?: TypographyPresentationProps;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    servicesGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    // XNG-Change
    shipSelect?: SelectPresentationProps;
    methodGroup?: CheckboxGroupPresentationProps;
    methodsBox?: InjectableCss;
    carrierLine?: InjectableCss;
    notify?: InjectableCss;
}

export const onePageCheckoutDeliveryMethodStyles: OnePageCheckoutDeliveryMethodStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
    fulfillmentMethodText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    servicesGridItem: { width: 12 },
    deliveryDateGridItem: { width: 12 },
    pickUpDateGridItem: { width: 12 },
};

const styles = onePageCheckoutDeliveryMethodStyles;

export const CHECKOUT_DELIVERY_FORM_ID = "CHECKOUT_DELIVERY_FORM_ID";

const OnePageCheckoutDeliveryMethod = ({
    cart,
    isCartLoading,
    cartUrl,
    pickUpWarehouse,
    showShippingMethod,
    showRequestPickUpDate,
    showRequestDeliveryDate,
    maximumDeliveryPeriod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setShippingMethod,
    allowEmptyShipping,
    // XNG-Change
    shipPartial,
    setShipPartial,
    isGuestUser,
    deliveryAccount,
    setDeliveryAccount,
    collectSettingId,
}: Props) => {
    // XNG-change
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);
    useEffect(() => {
        if (cart) {
            setSelectedShipVia(cart.shipVia);
            setIsUpdatingCart(false);
            // XNG-Change
            setSelectedCarrier(cart?.carrier);
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => {
        if (cart?.properties?.pscShipPartial && !isGuestUser) {
            setShipPartial(parseBoolean(cart.properties.pscShipPartial));
        }
    }, [cart?.properties?.pscShipPartial]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return null;
        // <StyledWrapper {...styles.centeringWrapper}>
        //     <LoadingSpinner data-test-selector="onePageCheckout_deliveryMethodLoading" />
        // </StyledWrapper>
    }

    // const serviceChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    //     if (!cart.carriers) {
    //         return;
    //     }

    //     const shipVias = flatMap(cart.carriers, o => o.shipVias).filter(o => !!o) as ShipViaDto[];
    //     const shipVia = shipVias.find(o => o.id === event.currentTarget.value);
    //     if (!shipVia) {
    //         return;
    //     }

    //     const carrier = cart.carriers.find(o => o.shipVias?.some(p => p.id === shipVia.id));
    //     if (!carrier) {
    //         return;
    //     }

    //     setIsUpdatingCart(true);
    //     setSelectedShipVia(shipVia);
    //     setShippingMethod({ carrier, shipVia });
    // };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    // XNG-Change
    const changeShipVia = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const id = event.target.value;
        const shipVia = selectedCarrier.shipVias?.find(shipVia => shipVia.id === id);
        if (!shipVia) {
            return;
        }
        setShippingMethod({ carrier: selectedCarrier, shipVia });
        setSelectedShipVia(shipVia);
    };

    const changeCarrier = (carrier: CarrierDto) => {
        if (!carrier.shipVias?.[0]) {
            return;
        }
        setShippingMethod({ carrier, shipVia: carrier.shipVias[0] });
        setSelectedCarrier(carrier);
        setSelectedShipVia(carrier?.shipVias[0]);
    };

    const IsHazardousGood = cart.cartLines && cart.cartLines.some(line => (line as any).isHazardousGood);
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem}>
                <Typography as="h2" {...styles.titleText}>
                    {showShippingMethod ? translate("Shipping Details") : translate("Pick Up")}
                </Typography>
            </GridItem>
            {selectedCarrier?.id !== collectSettingId &&
                !!siteMessage("Custom_Freight_Disclaimer_Note").toString().length && (
                    <GridItem width={12}>
                        <Notify type="info" {...styles.notify}>
                            {siteMessage("Custom_Freight_Disclaimer_Note")}
                        </Notify>
                    </GridItem>
                )}
            {IsHazardousGood && (
                <GridItem width={12}>
                    <Notify type="info" {...styles.notify}>
                        {siteMessage("Custom_Is_Hazardous_Good_Note")}
                    </Notify>
                </GridItem>
            )}
            {/* <GridItem {...styles.editGridItem}>
                <Link {...styles.editLink} href={cartUrl} data-test-selector="deliveryMethod_editButton">
                    {translate("Edit")}
                </Link>
            </GridItem>
            <GridItem {...styles.fulfillmentMethodGridItem}>
                <Typography
                    as="p"
                    {...styles.fulfillmentMethodText}
                    data-test-selector="onePageCheckout_fulfillmentMethod"
                >
                    {showShippingMethod
                        ? translate("Ship to home")
                        : `${translate("Store pickup at")} ${pickUpWarehouse?.description || pickUpWarehouse?.name}`}
                </Typography>
            </GridItem> */}
            {cart.carriers?.length === 0 && !allowEmptyShipping && (
                <GridItem {...styles.noCarriersFoundGridItem}>
                    <Typography {...styles.noCarriersFoundText}>
                        {siteMessage("ReviewAndPay_NoCarriersFound")}
                    </Typography>
                </GridItem>
            )}
            {cart.carriers.length > 0 && (
                <GridItem {...styles.servicesGridItem}>
                    {/* <Select
                        required
                        onChange={serviceChangeHandler}
                        value={selectedShipVia?.id}
                        data-test-selector="onePageCheckout_chooseDeliveryMethod"
                        aria-label="Delivery Method"
                        label={translate("Delivery Method")}
                    >
                        {flatMap(cart.carriers, carrier =>
                            carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                        ).map(shipVia => (
                            <option
                                {...styles.serviceRadio}
                                key={shipVia?.id}
                                value={shipVia?.id}
                                data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                            >
                                {shipVia?.description}
                            </option>
                        ))}
                    </Select> */}
                    <CheckboxGroup
                        label={translate("Shipping Method")}
                        aria-label="Delivery Method"
                        {...styles.methodGroup}
                        //  XNG-Change
                        data-test-selector="shipping_method_CheckboxGroup"
                    >
                        <StyledBox {...styles.methodsBox}>
                            {cart.carriers.map(carrier => (
                                <StyledWrapper key={carrier.id || ""} {...styles.carrierLine}>
                                    <Checkbox
                                        onChange={() => changeCarrier(carrier)}
                                        checked={carrier.id === selectedCarrier?.id}
                                        //  XNG-Change
                                        data-test-selector={`${carrier.description}_shipping_method`}
                                    >
                                        {carrier.description}
                                    </Checkbox>
                                    {carrier.id === selectedCarrier?.id && (
                                        <RadioGroup
                                            onChangeHandler={changeShipVia}
                                            horizontal
                                            value={selectedShipVia?.id}
                                            //  XNG-Change
                                            data-test-selector="ship_via_RadioGroup"
                                        >
                                            {selectedCarrier.shipVias?.map(shipVia => (
                                                <Radio
                                                    value={shipVia?.id}
                                                    key={shipVia.id || ""}
                                                    aria-label={shipVia.description}
                                                    //  XNG-Change
                                                    data-test-selector={`${shipVia.description}_ship_via`}
                                                >
                                                    {shipVia.description}
                                                </Radio>
                                            ))}
                                        </RadioGroup>
                                    )}
                                </StyledWrapper>
                            ))}
                            {selectedCarrier?.id === collectSettingId && (
                                <>
                                    <TextField
                                        required
                                        label={translate("Account Carrier")}
                                        aria-label={translate("Account Carrier")}
                                        value={deliveryAccount.carrier}
                                        onChange={e => setDeliveryAccount({ carrier: e.target.value })}
                                        formId={CHECKOUT_DELIVERY_FORM_ID}
                                        // XNG-Change
                                        placeholder={translate("Account Carrier")}
                                    />
                                    <TextField
                                        required
                                        label={translate("Account Number")}
                                        aria-label={translate("Account Number")}
                                        value={deliveryAccount.number}
                                        onChange={e => setDeliveryAccount({ number: e.target.value })}
                                        formId={CHECKOUT_DELIVERY_FORM_ID}
                                        // XNG-Change
                                        placeholder={translate("Account Number")}
                                    />
                                </>
                            )}
                        </StyledBox>
                    </CheckboxGroup>

                    <Select
                        value={`${shipPartial}`}
                        data-test-selector="partialShip"
                        label="Order Shipment Type"
                        required
                        onChange={e => setShipPartial(parseBoolean(e.currentTarget.value))}
                        {...styles.shipSelect}
                    >
                        <option value="false">{translate("Complete Shipment")}</option>
                        <option value="true">{translate("Ship When Available")}</option>
                    </Select>
                    {/* <RadioGroup
                        {...styles.servicesRadioGroup}
                        label={translate("Choose delivery method:")}
                        value={selectedShipVia?.id}
                        disabled={isCartLoading || isUpdatingCart}
                        onChangeHandler={serviceChangeHandler}
                        data-test-selector="onePageCheckout_chooseDeliveryMethod"
                    >
                        {flatMap(cart.carriers, carrier =>
                            carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                        ).map(shipVia => (
                            <Radio
                                {...styles.serviceRadio}
                                key={shipVia?.id}
                                value={shipVia?.id}
                                data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                            >
                                {shipVia?.carrier.description} - {shipVia?.description}
                            </Radio>
                        ))}
                    </RadioGroup> */}
                </GridItem>
            )}
            {showShippingMethod && showRequestDeliveryDate && (
                <GridItem {...styles.deliveryDateGridItem} data-test-selector="onePageCheckout_requestedDeliveryDate">
                    <DatePicker
                        {...styles.deliveryDatePicker}
                        label={translate("Requested Shipment Date")}
                        hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                        selectedDay={cart.requestedDeliveryDateDisplay!}
                        dateTimePickerProps={{
                            minDate: addDays(new Date(), 1),
                            maxDate: addDays(new Date(), maximumDeliveryPeriod),
                            monthAriaLabel: translate("Request delivery date month"),
                            dayAriaLabel: translate("Request delivery date day"),
                            yearAriaLabel: translate("Request delivery date year"),
                            ...styles.deliveryDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={handleRequestDeliveryDateChanged}
                        {...{ isOptional: true }}
                    />
                </GridItem>
            )}
            {showRequestPickUpDate && (
                <GridItem {...styles.pickUpDateGridItem} data-test-selector="onePageCheckout_requestPickUpDate">
                    <DatePicker
                        {...styles.pickUpDatePicker}
                        label={translate("Request Pick Up Date")}
                        hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                        selectedDay={cart.requestedPickupDateDisplay!}
                        dateTimePickerProps={{
                            minDate: new Date(),
                            maxDate: addDays(new Date(), maximumDeliveryPeriod),
                            ...styles.pickUpDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={handleRequestPickUpDateChanged}
                        monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter month, format M M",
                        )}`}
                        dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter day, format D D",
                        )}`}
                        yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter year, format Y Y Y Y",
                        )}`}
                    />
                </GridItem>
            )}
            {/* XNG-Change */}

            <GridItem width={12}>
                {createWidgetElement("OnePageCheckout/OnePageCheckoutOrderNotesEntry", { fields: {} })}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDeliveryMethod),
    definition: {
        displayName: "Delivery Method",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
